import BankData from "@/generated/bank.json";
import { BankCode } from "@/libs/constants";

const DEFAULT_COLOR_CODE = "#00c1ff";
const FALLBACK_BANK = "mfbank";

export enum Env {
  Prod = "prod",
  Stg = "stg",
  LocalDev = "localDev",
}

export type BankDef = {
  bankCode: string;
  jpName: string;
  jpNickname: string;
  jpAlias: string;
  jpFormalName: string;
  subDomain: string;
  colorCode: string;

  termsRegulationDate: string;
  termsRevisionDate: string;
  dataProvisionRegulationDate: string;
  dataProvisionRevisionDate: string;
  dataProvisionAdditionalProviders: string[];

  app?: string;
  billType: string;
  externalIDPs: BaseExternalIDP[];
  termsClientType: string;
  redirectPageAfterSignOut: string;
  migrationFromDate?: string;
  migrationToDate?: string;

  bankEnv: {
    [Property in Env]: BankEnv;
  };
};
// do not remove, this verifies that bank.json satisfies the BankDef type
const bankDefTypeCheck = BankData.mfbank satisfies BankDef;

export type BankEnv = {
  cognito: {
    userPoolID: string;
    backendClientID: string;
    webClientID: string;
  };
  exitSurveyGoogleForm: {
    prefilledLinkURL: string;
    displayIDParam: string;
  };
  externalIDPs: ExternalIDP[];
  featureFlag: {
    collectCancelReason: boolean;
    kenallAddressAssist: boolean;
  };
  aegisEnabled: boolean;
  landingPageURL: {
    bfm: string;
    dxf: string;
    invoice: string;
  };
  termsURL: {
    bfm: string;
    dxf: string;
    invoice: string;
  };
  billingURL: string;
  xidURL: string;
};

export type ExternalIDPName = "bizsol" | "connectBoard";

export type ExternalIDP = BizsolExternalIDP | ConnectBoardExternalIDP;

export interface BaseExternalIDP {
  name: ExternalIDPName;
  displayName: string;
}

export interface BizsolExternalIDP extends BaseExternalIDP {
  name: "bizsol";
  displayName: string;

  clientID: string;
  redirectURI: string;
  fqdn: string;
  anserFQDN: string;
}

export interface ConnectBoardExternalIDP extends BaseExternalIDP {
  name: "connectBoard";
  displayName: string;

  clientID: string;
  applicationID: string;
  redirectURI: string;
  fqdn: string;
  azureADB2CFQDN: string;
  azureADB2CTenantName: string;
  azureADB2CPolicyName: string;
}

export type CancelReasonGFormInfo = {
  PrefilledLink: string;
  DisplayIdParam: string;
};

const getEnv = (): Env => {
  const env = process.env.NEXT_PUBLIC_STAGE;
  if (env == Env.Prod || env == Env.Stg || env == Env.LocalDev) {
    return env;
  }
  return Env.Prod;
};
const isProd = (): boolean => getEnv() === Env.Prod;

const overrideVar = <T>(name: string, defaultVal: T) => {
  let overrideVal: T | undefined = undefined;

  const get = (): T => {
    if (getEnv() == Env.Prod) {
      return defaultVal;
    }
    if (!overrideVal) {
      return defaultVal;
    }
    return overrideVal;
  };

  const setOverride = (newOverrideVal: T) => {
    if (getEnv() == Env.Prod) {
      console.error(
        `setOverride${name}_DEV_AND_TEST_ONLY() called in production! This function should not be called in production!`
      );
      return;
    }
    overrideVal = newOverrideVal;
  };

  return {
    get,
    setOverride,
  };
};

const getBankName = (): string => {
  // keep in sync with router.go:getAPPJS()
  // keep in sync with web/package.json:.scripts.build
  // keep in sync with web/libs/config.ts
  // keep in sync with web_bank/libs/config.ts
  //
  // We only check for a prefix to prevent router.go from replacing this.
  // DO NOT CHANGE THIS TO THE FULL PLACEHOLDER STRING.
  const placeholderBankNamePrefix = "MFXID_FAKE_PLACEHOLDER_BANK_NAME";

  const envBankName = process.env.NEXT_PUBLIC_BANK_NAME;

  // something has gone horribly wrong, NEXT_PUBLIC_BANK_NAME must be set and must be a string
  // return FALLBACK_BANK so we still do something sane
  if (!envBankName || typeof envBankName !== "string") return FALLBACK_BANK;

  // we are being built by npm run build, return FALLBACK_BANK for static rendering
  if (envBankName.startsWith(placeholderBankNamePrefix)) return FALLBACK_BANK;

  // NEXT_PUBLIC_BANK_NAME is set, it may or may not be a valid bank name
  // but that is not the concern of this function, just return it
  return envBankName;
};

const getBankDef = (bankIdentifier: string): BankDef => {
  if (BankData[bankIdentifier]) {
    return BankData[bankIdentifier];
  }
  return BankData[FALLBACK_BANK];
};

const getIDP = (
  bankEnv: BankEnv,
  name: ExternalIDPName
): ExternalIDP | undefined => {
  const idp = bankEnv.externalIDPs.find((idp) => idp.name === name);
  return idp;
};

// bankDef, bankEnv, bizsolEnv and connectBoardEnv return, for the current env/bankCode, the respective values
const bankDef = () => getBankDef(getBankName());
const bankEnv = () => bankDef().bankEnv[getEnv()];
const bizsolEnv = (): BizsolExternalIDP => {
  const bizsol = getIDP(bankEnv(), "bizsol");
  if (bizsol) return bizsol as BizsolExternalIDP;
  return {
    name: "bizsol",
    displayName: "",
    clientID: "",
    redirectURI: "",
    fqdn: "",
    anserFQDN: "",
  };
};
const connectBoardEnv = (): ConnectBoardExternalIDP => {
  const connectBoard = getIDP(bankEnv(), "connectBoard");
  if (connectBoard) return connectBoard as ConnectBoardExternalIDP;
  return {
    name: "connectBoard",
    displayName: "",
    clientID: "",
    applicationID: "",
    redirectURI: "",
    fqdn: "",
    azureADB2CFQDN: "",
    azureADB2CTenantName: "",
    azureADB2CPolicyName: "",
  };
};

const { get: getBankNickname, setOverride: setOverrideBankNickname } =
  overrideVar<string>("BankNickname", bankDef().jpNickname);
const { get: getBankFormalName, setOverride: setOverrideBankFormalName } =
  overrideVar<string>("BankFormalName", bankDef().jpFormalName);
const { get: getBizsolDisplayName, setOverride: setOverrideBizsolDisplayName } =
  overrideVar<string>("BizsolDisplayName", bizsolEnv().displayName);

// TODO: we need to rework the Storybooks that depended on this, export a dummy function for now
export const setOverrideBankCode_DEV_AND_TEST_ONLY = (
  newOverrideVal: string
) => {};
export const setOverrideBankName_DEV_AND_TEST_ONLY = (
  newOverrideVal: string
) => {};
export const setOverrideBizsolDisplayName_DEV_AND_TEST_ONLY =
  setOverrideBizsolDisplayName;
export const setOverrideBankNickname_DEV_AND_TEST_ONLY =
  setOverrideBankNickname;
export const setOverrideBankFormalName_DEV_AND_TEST_ONLY =
  setOverrideBankFormalName;

export default {
  get STAGE(): string {
    return getEnv();
  },
  get ROLLBAR_ENV(): string {
    return getEnv();
  },
  get IS_PROD(): boolean {
    return isProd();
  },
  get BANK_CODE(): BankCode {
    return bankDef().bankCode as BankCode;
  },
  get BANK_NAME(): string {
    return bankDef().jpName;
  },
  get BANK_NICKNAME(): string {
    return bankDef().jpNickname;
  },
  get BANK_SYSTEM_NAME(): string {
    return getBankName();
  },
  get BANK_FORMAL_NAME(): string {
    return bankDef().jpFormalName;
  },
  get BANK_COLOR(): string {
    // keep in sync with web_bank/libs/config.ts
    const colorCode = parseHexColor(bankDef().colorCode);
    if (colorCode !== "") return colorCode;

    console.error(
      `${bankDef().bankCode} has an invalid bank color code ${
        bankDef().colorCode
      }`
    );
    return DEFAULT_COLOR_CODE;
  },
  get REGULATION_DATE(): string {
    return bankDef().termsRegulationDate;
  },
  get REVISION_DATE(): string {
    return bankDef().termsRevisionDate;
  },
  // DATA_PROVISION_REGULATION_DATE is the date the data provision policy was introduced.
  get DATA_PROVISION_REGULATION_DATE(): string {
    return bankDef().dataProvisionRegulationDate;
  },
  // DATA_PROVISION_REVISION_DATE is the date the data provision policy was last revised.
  get DATA_PROVISION_REVISION_DATE(): string {
    return bankDef().dataProvisionRevisionDate;
  },
  get IS_SHIKOKUBANK(): boolean {
    return bankDef().bankCode === BankCode.ShikokuBank;
  },
  get IS_SHIGAGIN(): boolean {
    return bankDef().bankCode === BankCode.Shigagin;
  },
  get IS_KYOSHIN(): boolean {
    return bankDef().bankCode === BankCode.Kyoshin;
  },
  get IS_KYOTOBANK(): boolean {
    return bankDef().bankCode === BankCode.KyotoBank;
  },
  get IS_CHUGIN(): boolean {
    return bankDef().bankCode === BankCode.Chugin;
  },
  get IS_DHBK(): boolean {
    return bankDef().bankCode === BankCode.DHBK;
  },

  get BIZSOL_DISPLAY_NAME(): string {
    return getBizsolDisplayName();
  },
  get BIZSOL_REDIRECT_URI(): string {
    return bizsolEnv().redirectURI;
  },
  get BIZSOL_CLIENT_ID(): string {
    return bizsolEnv().clientID;
  },
  get BIZSOL_ANSER_FQDN(): string {
    return bizsolEnv().anserFQDN;
  },
  get BIZSOL_FQDN(): string {
    return bizsolEnv().fqdn;
  },
  get USING_BIZSOL(): boolean {
    const bankCode = bankDef().bankCode;
    return bankCode === BankCode.Kyoshin || bankCode === BankCode.ShikokuBank;
  },

  get CONNECT_BOARD_CLIENT_ID(): string {
    return connectBoardEnv().clientID;
  },
  get CONNECT_BOARD_APP_ID(): string {
    return connectBoardEnv().applicationID;
  },
  get CONNECT_BOARD_REDIRECT_URI(): string {
    return connectBoardEnv().redirectURI;
  },
  get CONNECT_BOARD_FQDN(): string {
    return connectBoardEnv().fqdn;
  },
  get CONNECT_BOARD_AZUREADB2C_FQDN(): string {
    return connectBoardEnv().azureADB2CFQDN;
  },
  get CONNECT_BOARD_AZUREADB2C_TENANT_NAME(): string {
    return connectBoardEnv().azureADB2CTenantName;
  },
  get CONNECT_BOARD_AZUREADB2C_POLICY_NAME(): string {
    return connectBoardEnv().azureADB2CPolicyName;
  },
  get CONNECT_BOARD_DISPLAY_NAME(): string {
    return connectBoardEnv().displayName;
  },
  get USING_CONNBOARD(): boolean {
    return bankDef().bankCode === BankCode.DHBK;
  },

  get USING_AEGIS(): boolean {
    return bankEnv().aegisEnabled;
  },

  get MIGRATION_FROM_DATE(): string | undefined {
    return bankDef().migrationFromDate;
  },

  get MIGRATION_TO_DATE(): string | undefined {
    return bankDef().migrationToDate;
  },

  get USING_EXTERNAL_IDP(): boolean {
    return this.USING_BIZSOL || this.USING_CONNBOARD;
  },
  // URL for the LP of BFM.
  get LP_URL_BFM(): string {
    return bankEnv().landingPageURL.bfm;
  },
  // URL for the LP of Works.
  get LP_URL_DXF(): string {
    return bankEnv().landingPageURL.dxf;
  },
  // URL for the LP of Invoice.
  get LP_URL_INVOICE(): string {
    return bankEnv().landingPageURL.invoice;
  },
  // 業種が保険業かどうかを判定するフラグ
  get IS_INDUSTRY_TYPE_INSURANCE(): boolean {
    return bankDef().bankCode === BankCode.Aioinissaydowa;
  },
  // 業種が銀行業かどうかを判定するフラグ
  get IS_INDUSTRY_TYPE_BANK(): boolean {
    return !this.IS_INDUSTRY_TYPE_INSURANCE;
  },
  get ADDITIONAL_PROVIDERS(): string[] {
    return bankDef().dataProvisionAdditionalProviders;
  },
  get SUBDOMAIN(): string {
    return bankDef().subDomain;
  },

  get APP(): string {
    const a = bankDef().app;
    return a !== undefined ? a : "";
  },
  get REDIRECT_URL_AFTER_SIGN_OUT(): string {
    return bankDef().redirectPageAfterSignOut;
  },
  get XID_URL(): string {
    return bankEnv().xidURL;
  },

  get TERMS_CLIENT_TYPE(): string {
    return bankDef().termsClientType;
  },
  get BFM_TERMS_OF_USE_URL(): string {
    return bankEnv().termsURL.bfm;
  },
  get DXF_TERMS_OF_USE_URL(): string {
    return bankEnv().termsURL.dxf;
  },
  get INVOICE_TERMS_OF_USE_URL(): string {
    return bankEnv().termsURL.invoice;
  },

  get USER_POOL_ID(): string {
    return bankEnv().cognito.userPoolID;
  },
  get COGNITO_CLIENT_ID(): string {
    return bankEnv().cognito.webClientID;
  },
  get COGNITO_SERVER_CLIENT_ID(): string {
    return bankEnv().cognito.backendClientID;
  },

  get FEATURE_FLAG_COLLECT_CANCEL_REASON(): boolean {
    return bankEnv().featureFlag.collectCancelReason;
  },
  get FEATURE_FLAG_KENALL_ADDRESS_ASSIST(): boolean {
    return bankEnv().featureFlag.kenallAddressAssist;
  },

  get ROLLBAR_ENABLED(): boolean {
    return getEnv() === Env.Prod || getEnv() === Env.Stg;
  },
  get ROLLBAR_CODE_VERSION(): string {
    return process.env.NEXT_PUBLIC_ROLLBAR_CODE_VERSION || Env.LocalDev;
  },

  get BILLING_URL(): string {
    return bankEnv().billingURL;
  },

  /**
   * Google Form info for Collect Cancel Reason
   * @returns Google Form info for Collect Cancel Reason
   */
  get CollectCancelReasonGFormInfo(): CancelReasonGFormInfo {
    return {
      PrefilledLink: bankEnv().exitSurveyGoogleForm.prefilledLinkURL,
      DisplayIdParam: bankEnv().exitSurveyGoogleForm.displayIDParam,
    };
  },
  get ExternalIDPs(): BaseExternalIDP[] {
    return bankDef().externalIDPs;
  },
};

// keep in sync with web_bank/libs/config.ts
export const parseHexColor = (color: string): string => {
  const hexColorRegex = /^#?[0-9A-Fa-f]{6}$/;

  if (!hexColorRegex.test(color)) return "";

  return color[0] === "#" ? color : `#${color}`;
};
